import i18n from '@/utils/i18n';
import React, { useState } from 'react';
import {
  Alert,
  AlertCardType,
} from '@/experiences/sales-portal/domain/insights/blocs/types/alert';
import { AlertCard } from '@/experiences/sales-portal/domain/insights/components/alert-card/alert-card';
import { CategoryTogglePill } from '@/experiences/sales-portal/domain/insights/components/category-toggle-pill';
import { createStyles } from '@/contexts/theme-context';
import { Platform, View } from 'react-native';
import { Separator, Spacer } from '@usepepper/sprout-kit-ui';
import { useAlertPress } from '@/experiences/sales-portal/domain/insights/blocs/hooks/use-alert-press';
import type { AlertCategory } from '@/experiences/sales-portal/domain/insights/blocs/transformers/alert-transformer';

/** Cards shown per category before the rest are truncated behind the pill. */
const COLLAPSED_VISIBLE_COUNT = 1;

export interface AlertFeedProps {
  categories: AlertCategory[];
  onDelete: (alert: Alert) => void;
  onHide: (alert: Alert) => void;
  supplierUUID: string;
}

export const AlertFeed: React.FC<AlertFeedProps> = ({
  categories,
  onDelete,
  onHide,
  supplierUUID,
}) => {
  const [styles, theme] = useStyles();
  const { onAccountDetailPress, onAlertPress } = useAlertPress(supplierUUID);
  const [expandedCategories, setExpandedCategories] = useState<
    Set<AlertCardType>
  >(new Set());

  const getAlertActions = (alert: Alert) => [
    {
      key: 'hide',
      label: i18n.t('insights_hub.action_sheet.hide'),
      onPress: () => onHide(alert),
    },
    {
      key: 'delete',
      label: i18n.t('delete'),
      onPress: () => onDelete(alert),
    },
  ];

  const toggleCategory = (cardType: AlertCardType) => {
    setExpandedCategories((previous) => {
      const next = new Set(previous);
      if (next.has(cardType)) {
        next.delete(cardType);
      } else {
        next.add(cardType);
      }
      return next;
    });
  };

  const categoryRows = categories.map((category) => {
    const isExpanded = expandedCategories.has(category.cardType);
    return {
      cardType: category.cardType,
      isExpanded,
      isTruncated: category.alerts.length > COLLAPSED_VISIBLE_COUNT,
      totalCount: category.alerts.length,
      visibleAlerts: isExpanded
        ? category.alerts
        : category.alerts.slice(0, COLLAPSED_VISIBLE_COUNT),
    };
  });

  return (
    <View style={styles.container}>
      {categoryRows.map((category, categoryIndex) => (
        <React.Fragment key={category.cardType}>
          {categoryIndex > 0 ? <Separator /> : null}

          <View
            style={
              category.isExpanded ? styles.alertContainerExpanded : undefined
            }
          >
            {category.visibleAlerts.map((alert) => (
              <AlertCard
                actions={getAlertActions(alert)}
                alert={alert}
                key={alert.alertUUID}
                onAlertCardPress={onAccountDetailPress(alert)}
                onContentPress={onAlertPress(alert)}
              />
            ))}
            {category.isTruncated ? (
              <View style={styles.pillRow}>
                <CategoryTogglePill
                  count={category.totalCount}
                  expanded={category.isExpanded}
                  onPress={() => toggleCategory(category.cardType)}
                />
              </View>
            ) : (
              <Spacer size={theme.sk.spacing.spaceMedium} />
            )}
          </View>
        </React.Fragment>
      ))}
    </View>
  );
};

const useStyles = createStyles((theme) => ({
  alertContainerExpanded: {
    gap: theme.sk.spacing.spaceMedium,
  },
  container: {
    backgroundColor: theme.sk.color.bg.primary.default,
    borderColor: theme.sk.color.border.primary,
    borderRadius: theme.sk.radii.radiusLarge,
    borderWidth: 1,
    overflow: 'hidden',
  },
  pillRow: {
    alignItems: 'center',
    // TODO(SRG-339): remove the platform-conditional value once SKUI resolves
    // tokens per breakpoint.
    paddingVertical:
      Platform.OS === 'web'
        ? theme.sk.spacing.spaceSmall
        : theme.sk.spacing.spaceXsmall,
  },
}));
